/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { FC, useState, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from '../../../../states/containers/store'
import { useTranslation } from 'react-i18next'
import Modal from '../../common/Modal'
import { cloneDeep, concat, filter, pull, sortBy, includes, orderBy } from 'lodash'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useAlert } from 'react-alert'
import { createAdministrator, fetchAdministratorList } from '../../../../states/containers/setting/systemSlice'
import { compile } from '../../../../utils/search'

interface SimpleMember {
  id: number
  name: string
  email: string
  contract: string
  place: string
}

interface MemberModalState {
  display: boolean,
  type: string,
}

interface Props {
  editModalState: MemberModalState
  closeCallback: VoidFunction
  closeable?: boolean
}

const EditModal: FC<Props> = (props) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const alert = useAlert()

  const {
    teamList,
    system,
    login,
  } = useSelector((state: RootState) => state)

  const {
    editModalState,
    closeCallback,
    closeable,
  } = props

  const [selectMemberIds, setSelectMemberIds] = useState<number[]>([])
  const [joinedMemberIds, setJoinedMemberIds] = useState<number[]>([])
  const [displayModal, setDisplayModal] = useState<boolean>(false)

  const [memberList, setMemberList] = useState<(SimpleMember | null)[]>()
  const [memberListInit, setMemberListInit] = useState<(SimpleMember | null)[]>()
  const [searchWord, setSearchWord] = useState<string>("")

  const search = (words: string) => {
    setSearchWord(words)
    if (!words) {
      setMemberList(cloneDeep(memberListInit))
      return
    }
    const word = words.split(/\s+/)
    const list = filter(memberListInit, (member) => {
      if (member === null) return false
      return compile(word).test(member.name + member.email) || includes(selectMemberIds, member.id)
    }) as SimpleMember[]
    setMemberList(sortBy(list, 'id'))
  }

  const reset = () => {
    setSearchWord("")
    setMemberList(cloneDeep(memberListInit))
  }

  const closeModal = () => {
    closeCallback()
    reset()
    setSelectMemberIds([])
  }

  const saveAdmin = () => {
    dispatch(createAdministrator(
      {
        member_ids: selectMemberIds,
      },
      () => {
        dispatch(fetchAdministratorList())
        alert.success(t('setting.admin.message.add'))
      },
    ))
    closeModal()
  }

  const clickCheckbox = (memberId: number) => {
    if (!memberId || includes(joinedMemberIds, memberId)) return
    if (includes(selectMemberIds, memberId)) {
      setSelectMemberIds(pull(selectMemberIds, memberId))
    } else {
      setSelectMemberIds(concat(selectMemberIds, memberId))
    }
    search(searchWord)
  }

  useEffect(() => {
    setDisplayModal(editModalState.display)
  }, [editModalState.display])

  useEffect(() => {
    if (!teamList) return
    setMemberList(orderBy(teamList.full_members, ['id'], ['asc']))
    setMemberListInit(orderBy(teamList.full_members, ['id'], ['asc']))
  }, [teamList])

  useEffect(() => {
    if (!system.administrator_list) return
    setJoinedMemberIds(system.administrator_list?.map((admin) => admin.id))
  }, [system.administrator_list])

  return (
    <Modal
      title={t('setting.admin.add')}
      show={displayModal}
      wrapperClass="setting_admin"
      closeCallback={closeModal}
      closeable={closeable}
    >
      <>
        <div className="item">
          <div className="modal-keyword-search">
            <div className="title">
              {t('setting.admin.create_modal.title')}
            </div>
            <div
              className="search_box"
              style={{
                position: "relative",
                top: 0,
                border: "none",
                boxShadow: "none",
                padding: 0,
              }}
            >
              <input
                type="text"
                placeholder={t('setting.admin.create_modal.search.placeholder')}
                onChange={(event) => search(event.target.value)}
                value={searchWord}
              />
              {
                searchWord && (
                  <button type="button" className="reset" onClick={reset}>
                    <FontAwesomeIcon icon={['far', 'times-circle']} />
                  </button>
                )
              }
            </div>
          </div>
          <div className="detail row_1">
            <table className="modal-flex-table admin">
              <thead>
                <tr>
                  <th>{t('setting.admin.table.add')}</th>
                  <th>{t('setting.admin.table.name')}</th>
                  <th>{t('setting.admin.table.email')}</th>
                </tr>
              </thead>
              <tbody>
                {
                  memberList && memberList.map((member: SimpleMember) => (
                    <tr
                      key={member.id}
                      className={includes(joinedMemberIds, member.id) ? "disable" : ""}
                      onClick={() => clickCheckbox(member.id)}
                    >
                      <td className="modal-table-checkbox">
                        <input
                          name={`member_id_${member.id}`}
                          id={`member_id_${member.id}`}
                          value={member.id}
                          checked={includes(selectMemberIds, member.id)}
                          type="checkbox"
                          disabled={includes(joinedMemberIds, member.id)}
                          readOnly
                        />
                      </td>
                      <td className="modal-table-name">
                        {member.name}
                      </td>
                      <td className="modal-table-mail">
                        {member.email}
                      </td>
                    </tr>
                  ))
                }
              </tbody>
            </table>
            {
              login.function_enabled_state?.task_management && (
                <div
                  style={{
                    marginTop: "16px",
                    color: "#FF0000",
                  }}
                >
                  ※ 工数管理のプロジェクト設定権限も同時に作成されます
                </div>
              )
            }
          </div>
        </div>
      </>
      <>
        <button type="button" className="button cancel" onClick={closeModal} disabled={!closeable}>
          {t('form.component.cancel')}
        </button>
        <button
          type="button"
          className="button"
          onClick={saveAdmin}
        >
          {t('setting.admin.table.add')}
        </button>
      </>
    </Modal>
  )
}

EditModal.defaultProps = {
  closeable: undefined,
}

export default EditModal
